import { useState, useEffect } from 'react';
import {
  Search, Filter, Check, X as XIcon, Star, Trash2,
  Loader2, Calendar, MapPin, Clock, ExternalLink, Globe, Plus, RefreshCw, CheckSquare, Square
} from 'lucide-react';
import { Button } from '@/components/common';

const API_URL = import.meta.env.VITE_API_URL || '';

const getToken = () => localStorage.getItem('auth_token');

interface Event {
  id: string;
  title: string;
  title_ko?: string;
  description?: string;
  description_ko?: string;
  category?: string;
  organizer?: string;
  venue?: string;
  city?: string;
  country_code?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  start_date?: string;
  end_date?: string;
  is_online: number;
  online_url?: string;
  ticket_url?: string;
  image_url?: string;
  price?: string;
  is_featured: number;
  status: 'pending' | 'approved' | 'rejected';
  source?: string;
  created_at: string;
}

function StatusBadge({ status }: { status: string }) {
  const badges: Record<string, { className: string; label: string }> = {
    pending: { className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Pending' },
    approved: { className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Approved' },
    rejected: { className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Rejected' },
  };

  const badge = badges[status] || badges.pending;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function SourceBadge({ source }: { source?: string }) {
  const badges: Record<string, { className: string; label: string }> = {
    community: { className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Community' },
    kcc: { className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'KCC' },
    eventbrite: { className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: 'Eventbrite' },
  };

  const badge = badges[source || 'community'] || badges.community;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

export default function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Modal states
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'delete' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Multi-select
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelectAll = () => {
    if (selectedEvents.size === events.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(events.map(e => e.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEvents(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedEvents.size === 0) return;
    if (!confirm(`Delete ${selectedEvents.size} selected event(s)?`)) return;

    setBulkDeleting(true);
    try {
      const deletePromises = Array.from(selectedEvents).map(id =>
        fetch(`${API_URL}/api/admin/events/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getToken()}` },
        })
      );
      await Promise.all(deletePromises);
      setMessage({ type: 'success', text: `${selectedEvents.size} event(s) deleted` });
      setSelectedEvents(new Set());
      fetchEvents();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete some events' });
    } finally {
      setBulkDeleting(false);
    }
  };

  // New event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    title_ko: '',
    description: '',
    category: 'cultural',
    venue_name: '',
    city: '',
    country: 'United States',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_free: true,
    website_url: '',
    image_url: '',
    organizer_name: '',
  });

  const limit = 20;

  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (sourceFilter) params.set('source', sourceFilter);

      const response = await fetch(`${API_URL}/api/admin/events?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [page, search, statusFilter, sourceFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchEvents();
  };

  const openActionModal = (event: Event, type: 'approve' | 'reject' | 'delete') => {
    setSelectedEvent(event);
    setActionType(type);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleStatusAction = async () => {
    if (!selectedEvent || !actionType) return;
    setActionLoading(true);

    try {
      if (actionType === 'delete') {
        const response = await fetch(`${API_URL}/api/admin/events/${selectedEvent.id}?reason=${encodeURIComponent(actionReason)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getToken()}` },
        });

        if (response.ok) {
          setMessage({ type: 'success', text: 'Event deleted successfully' });
          setShowActionModal(false);
          fetchEvents();
        } else {
          const error = await response.json();
          setMessage({ type: 'error', text: error.error || 'Delete failed' });
        }
      } else {
        const response = await fetch(`${API_URL}/api/admin/events/${selectedEvent.id}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: actionType === 'approve' ? 'approved' : 'rejected',
            reason: actionReason,
          }),
        });

        if (response.ok) {
          setMessage({ type: 'success', text: `Event ${actionType}d successfully` });
          setShowActionModal(false);
          fetchEvents();
        } else {
          const error = await response.json();
          setMessage({ type: 'error', text: error.error || 'Action failed' });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Action failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFeatureToggle = async (event: Event) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/events/${event.id}/feature`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_featured: event.is_featured === 0 }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Event featured status updated' });
        fetchEvents();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Update failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Update failed' });
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalPages = Math.ceil(total / limit);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/events/sync/force`, {
        method: 'POST',
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Sync complete: ${data.stats.added} added, ${data.stats.updated} updated`
        });
        fetchEvents();
      } else {
        setMessage({ type: 'error', text: data.error || 'Sync failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleAddEvent = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newEvent),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Event created successfully' });
        setShowAddModal(false);
        setNewEvent({
          title: '',
          title_ko: '',
          description: '',
          category: 'cultural',
          venue_name: '',
          city: '',
          country: 'United States',
          start_date: '',
          end_date: '',
          start_time: '',
          end_time: '',
          is_free: true,
          website_url: '',
          image_url: '',
          organizer_name: '',
        });
        fetchEvents();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to create event' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create event' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Event Moderation
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Review and approve community events
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync KCC'}
          </Button>
          <Button
            className="bg-coral-500 hover:bg-coral-600"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
          message.type === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or description..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
          >
            <option value="">All Sources</option>
            <option value="community">Community</option>
            <option value="kcc">KCC</option>
            <option value="eventbrite">Eventbrite</option>
          </select>
          <Button type="submit" className="bg-coral-500 hover:bg-coral-600">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </form>
      </div>

      {/* Bulk Actions */}
      {selectedEvents.size > 0 && (
        <div className="bg-coral-50 dark:bg-coral-900/20 rounded-xl p-4 mb-6 flex items-center justify-between">
          <span className="text-coral-700 dark:text-coral-300 font-medium">
            {selectedEvents.size} event(s) selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedEvents(new Set())}
            >
              Clear Selection
            </Button>
            <Button
              size="sm"
              className="bg-red-500 hover:bg-red-600"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No events yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Events will appear here when users submit them or when synced from external sources.
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Try Sync
              </Button>
              <Button
                className="bg-coral-500 hover:bg-coral-600"
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Select All Header */}
            <div className="px-4 py-3 border-b dark:border-slate-700 flex items-center gap-3 bg-gray-50 dark:bg-slate-700/50">
              <button
                onClick={toggleSelectAll}
                className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors"
              >
                {selectedEvents.size === events.length ? (
                  <CheckSquare className="w-5 h-5 text-coral-500" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedEvents.size === events.length ? 'Deselect all' : 'Select all'}
              </span>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              {events.map((event) => (
                <div key={event.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50">
                  <div className="flex gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(event.id)}
                      className="p-1 self-center flex-shrink-0"
                    >
                      {selectedEvents.has(event.id) ? (
                        <CheckSquare className="w-5 h-5 text-coral-500" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {/* Image */}
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt=""
                        className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-100 dark:bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white line-clamp-1">
                            {event.title}
                          </h3>
                          {event.organizer && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              By {event.organizer}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={event.status} />
                          <SourceBadge source={event.source} />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(event.start_date)}
                        </span>
                        {event.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {event.city}
                          </span>
                        )}
                        {event.is_online === 1 && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-4 h-4" />
                            Online
                          </span>
                        )}
                        {event.price && (
                          <span>{event.price}</span>
                        )}
                      </div>

                      {/* Badges */}
                      {event.is_featured === 1 && (
                        <span className="mt-2 inline-flex px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs items-center gap-1">
                          <Star className="w-3 h-3" />
                          Featured
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setSelectedEvent(event); setShowPreviewModal(true); }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                        title="Preview"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleFeatureToggle(event)}
                        className={`p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors ${
                          event.is_featured === 1 ? 'text-yellow-500' : 'text-gray-400'
                        }`}
                        title={event.is_featured === 1 ? 'Unfeature' : 'Feature'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      {event.status !== 'approved' && (
                        <button
                          onClick={() => openActionModal(event, 'approve')}
                          className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <Check className="w-4 h-4 text-green-500" />
                        </button>
                      )}
                      {event.status !== 'rejected' && (
                        <button
                          onClick={() => openActionModal(event, 'reject')}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <XIcon className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                      <button
                        onClick={() => openActionModal(event, 'delete')}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t dark:border-slate-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} events
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Event Preview
              </h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {/* Image */}
              {selectedEvent.image_url && (
                <img
                  src={selectedEvent.image_url}
                  alt=""
                  className="w-full h-48 object-cover rounded-xl mb-4"
                />
              )}

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {selectedEvent.title}
              </h3>
              {selectedEvent.title_ko && (
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {selectedEvent.title_ko}
                </p>
              )}

              {/* Details */}
              <div className="space-y-3 mb-4">
                {selectedEvent.organizer && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Organizer:</strong> {selectedEvent.organizer}
                  </p>
                )}
                <p className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {formatDate(selectedEvent.start_date)}
                  {selectedEvent.end_date && ` - ${formatDate(selectedEvent.end_date)}`}
                </p>
                {selectedEvent.venue && (
                  <p className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedEvent.venue}
                    {selectedEvent.city && `, ${selectedEvent.city}`}
                  </p>
                )}
                {selectedEvent.is_online === 1 && (
                  <p className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Online Event
                    {selectedEvent.online_url && (
                      <a
                        href={selectedEvent.online_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-coral-500 hover:underline"
                      >
                        Join Link
                      </a>
                    )}
                  </p>
                )}
                {selectedEvent.price && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Price:</strong> {selectedEvent.price}
                  </p>
                )}
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {selectedEvent.description}
                  </p>
                </div>
              )}

              {/* Links */}
              {selectedEvent.ticket_url && (
                <div className="mt-4">
                  <a
                    href={selectedEvent.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-coral-500 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Tickets
                  </a>
                </div>
              )}

              {/* Meta */}
              <div className="mt-6 pt-4 border-t dark:border-slate-700 flex flex-wrap gap-2">
                <StatusBadge status={selectedEvent.status} />
                <SourceBadge source={selectedEvent.source} />
                {selectedEvent.category && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                    {selectedEvent.category}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add New Event
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="Event title"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Title (Korean)
                  </label>
                  <input
                    type="text"
                    value={newEvent.title_ko}
                    onChange={(e) => setNewEvent({ ...newEvent, title_ko: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="이벤트 제목"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Description
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white resize-none"
                  placeholder="Event description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Category *
                  </label>
                  <select
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  >
                    <option value="cultural">Cultural</option>
                    <option value="performance">Performance</option>
                    <option value="exhibition">Exhibition</option>
                    <option value="film">Film</option>
                    <option value="workshop">Workshop</option>
                    <option value="lecture">Lecture</option>
                    <option value="festival">Festival</option>
                    <option value="food">Food</option>
                    <option value="language">Language</option>
                    <option value="social">Social</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    value={newEvent.city}
                    onChange={(e) => setNewEvent({ ...newEvent, city: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="e.g. Los Angeles"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Country *
                  </label>
                  <select
                    value={newEvent.country}
                    onChange={(e) => setNewEvent({ ...newEvent, country: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Japan">Japan</option>
                    <option value="South Korea">South Korea</option>
                    <option value="Singapore">Singapore</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newEvent.end_date}
                    onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Venue Name
                  </label>
                  <input
                    type="text"
                    value={newEvent.venue_name}
                    onChange={(e) => setNewEvent({ ...newEvent, venue_name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="e.g. Korean Cultural Center"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Organizer
                  </label>
                  <input
                    type="text"
                    value={newEvent.organizer_name}
                    onChange={(e) => setNewEvent({ ...newEvent, organizer_name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="e.g. KCCLA"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={newEvent.website_url}
                    onChange={(e) => setNewEvent({ ...newEvent, website_url: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={newEvent.image_url}
                    onChange={(e) => setNewEvent({ ...newEvent, image_url: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_free"
                  checked={newEvent.is_free}
                  onChange={(e) => setNewEvent({ ...newEvent, is_free: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-coral-500 focus:ring-coral-500"
                />
                <label htmlFor="is_free" className="text-sm text-gray-700 dark:text-gray-300">
                  Free event
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-coral-500 hover:bg-coral-600"
                  onClick={handleAddEvent}
                  disabled={actionLoading || !newEvent.title || !newEvent.city || !newEvent.start_date}
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Create Event
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {actionType === 'approve' ? 'Approve Event' : actionType === 'reject' ? 'Reject Event' : 'Delete Event'}
              </h2>
              <button
                onClick={() => setShowActionModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <h4 className="font-medium text-gray-900 dark:text-white line-clamp-1">
                  {selectedEvent.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {formatDate(selectedEvent.start_date)}
                  {selectedEvent.city && ` - ${selectedEvent.city}`}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Reason {actionType === 'approve' ? '(optional)' : '(recommended)'}
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : actionType === 'reject' ? 'rejecting' : 'deleting'} this event...`}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowActionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${
                    actionType === 'approve'
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                  onClick={handleStatusAction}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : actionType === 'approve' ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : actionType === 'reject' ? (
                    <XIcon className="w-4 h-4 mr-2" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
